/**
 * Employee Desktop — Documents RH
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  FolderOpen, Upload, Eye, Download, FileText, Loader2,
  AlertCircle, CheckCircle2, X, ChevronDown, Search
} from 'lucide-react';
import config from 'config';

const API = config.API_URL;

const DOC_TYPE_OPTIONS = [
  { value: 'SICK_LEAVE', label: 'Arrêt de travail' },
  { value: 'ADMIN_DOC', label: 'Document administratif' },
  { value: 'OTHER', label: 'Autre' },
];

const TYPE_BADGE = {
  CONTRACT: { label: 'Contrat', cls: 'text-blue-400 bg-blue-400/10' },
  AMENDMENT: { label: 'Avenant', cls: 'text-purple-400 bg-purple-400/10' },
  SICK_LEAVE: { label: 'Arrêt maladie', cls: 'text-amber-400 bg-amber-400/10' },
  ADMIN_DOC: { label: 'Admin', cls: 'text-emerald-400 bg-emerald-400/10' },
  OTHER: { label: 'Autre', cls: 'text-white/40 bg-white/5' },
};

export default function EmployeeDocumentsDesktopPage() {
  const { token } = useOutletContext();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [docType, setDocType] = useState('OTHER');
  const [label, setLabel] = useState('');
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState('');

  const fetchDocs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/employees/me/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    setMessage(null);
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('doc_type', docType);
    fd.append('label', label);
    try {
      const res = await fetch(`${API}/api/employees/me/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        setMessage({ ok: true, text: 'Document envoyé avec succès' });
        setShowUpload(false);
        setUploadFile(null);
        setLabel('');
        fetchDocs();
      } else {
        const err = await res.json();
        setMessage({ ok: false, text: err.detail || 'Erreur' });
      }
    } catch {
      setMessage({ ok: false, text: 'Erreur de connexion' });
    }
    setUploading(false);
  };

  const handleAction = async (docId, action) => {
    try {
      const res = await fetch(`${API}/api/employees/me/documents/${docId}/${action}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        window.open(data.url, '_blank');
      }
    } catch {}
  };

  const filtered = documents.filter(d =>
    !search || d.label?.toLowerCase().includes(search.toLowerCase()) || d.type?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" /></div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="desktop-documents-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-white">Documents RH</h1>
          <p className="text-white/40 text-sm mt-1">Vos documents administratifs et RH</p>
        </div>
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#C9A24D] text-black text-sm font-semibold hover:bg-[#d4af5a] transition"
          data-testid="desktop-upload-doc-btn"
        >
          <Upload className="w-4 h-4" />
          Envoyer un document
        </button>
      </div>

      {message && (
        <div className={`flex items-center gap-2 p-3 rounded-xl text-sm mb-6 ${message.ok ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
          {message.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Upload form */}
      {showUpload && (
        <form onSubmit={handleUpload} className="p-6 rounded-2xl bg-white/[0.03] border border-white/5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-end" data-testid="desktop-upload-form">
          <div>
            <label className="text-white/60 text-xs font-medium mb-1.5 block">Type</label>
            <div className="relative">
              <select value={docType} onChange={e => setDocType(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm appearance-none" data-testid="desktop-doc-type">
                {DOC_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-white/30 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="text-white/60 text-xs font-medium mb-1.5 block">Description</label>
            <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="Ex: Arrêt maladie mars 2026" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-white/20" data-testid="desktop-doc-label" />
          </div>
          <div>
            <label className="text-white/60 text-xs font-medium mb-1.5 block">Fichier</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => setUploadFile(e.target.files[0])} className="w-full text-white/60 text-sm file:mr-3 file:px-4 file:py-2 file:rounded-xl file:border-0 file:bg-[#C9A24D]/15 file:text-[#C9A24D] file:text-xs file:font-medium" data-testid="desktop-doc-file" />
          </div>
          <div className="md:col-span-3 flex gap-3">
            <button type="submit" disabled={!uploadFile || uploading} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#C9A24D] text-black text-sm font-semibold disabled:opacity-40 hover:bg-[#d4af5a] transition" data-testid="desktop-doc-submit">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Envoi...' : 'Envoyer'}
            </button>
            <button type="button" onClick={() => { setShowUpload(false); setUploadFile(null); }} className="px-5 py-2.5 rounded-xl text-white/40 text-sm hover:text-white transition">
              Annuler
            </button>
          </div>
        </form>
      )}

      {/* Search */}
      {documents.length > 0 && (
        <div className="relative mb-4">
          <Search className="absolute left-4 top-3 w-4 h-4 text-white/20" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un document..."
            className="w-full bg-white/[0.03] border border-white/5 rounded-xl pl-11 pr-4 py-2.5 text-white text-sm placeholder:text-white/20 focus:border-white/10 focus:outline-none"
            data-testid="desktop-doc-search"
          />
        </div>
      )}

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-2xl bg-white/[0.02] border border-white/5">
          <FolderOpen className="w-14 h-14 text-white/10 mb-4" />
          <p className="text-white/30 text-sm">{documents.length === 0 ? 'Aucun document' : 'Aucun résultat'}</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/5 overflow-hidden" data-testid="desktop-documents-table">
          <table className="w-full">
            <thead>
              <tr className="bg-white/[0.02]">
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Document</th>
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Type</th>
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Source</th>
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Date</th>
                <th className="text-right text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(doc => {
                const badge = TYPE_BADGE[doc.type] || TYPE_BADGE.OTHER;
                return (
                  <tr key={doc.id} className="border-t border-white/5 hover:bg-white/[0.02] transition" data-testid={`desktop-doc-row-${doc.id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-white/20" />
                        <span className="text-white text-sm font-medium">{doc.label || doc.original_filename}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="px-5 py-4 text-white/40 text-sm">{doc.uploaded_by === 'admin' ? 'Administration' : 'Vous'}</td>
                    <td className="px-5 py-4 text-white/40 text-sm">{new Date(doc.created_at).toLocaleDateString('fr-FR')}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleAction(doc.id, 'view')} className="p-2 rounded-lg text-white/30 hover:text-[#C9A24D] hover:bg-[#C9A24D]/10 transition" title="Voir" data-testid={`desktop-doc-view-${doc.id}`}>
                          <Eye className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleAction(doc.id, 'download')} className="p-2 rounded-lg text-white/30 hover:text-[#C9A24D] hover:bg-[#C9A24D]/10 transition" title="Télécharger" data-testid={`desktop-doc-dl-${doc.id}`}>
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
